// VAT on orders and VAT numbers (sculpt-api PLAN-payments.md §4, P9): how an order's VAT reads, what VIES said of a
// number, and the staff decision on a number VIES could not answer for.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { money } from '../services/format';
import { Badge, Button, Confirm, type Tone } from './ui';
import type { Order, VatDecision, VatStatus } from '../types/api';

/** ISO 3166-1 alpha-2 codes; names come from the browser (`Intl.DisplayNames`). */
const COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM ' +
  'CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW ' +
  'GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ' +
  'ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW ' +
  'PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG ' +
  'UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

/** Countries sorted by their name in `locale`. */
export function countries(locale: string): { code: string; name: string }[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale === 'fr' ? 'fr' : 'en'], { type: 'region' });
  } catch {
    names = null;
  }
  return COUNTRY_CODES.map((code) => ({ code, name: names?.of(code) ?? code })).sort((a, b) => a.name.localeCompare(b.name, locale));
}

const STATUS_TONE: Record<VatStatus, Tone> = { none: 'neutral', valid: 'good', invalid: 'bad', review: 'warn' };

export function VatStatusBadge({ status }: { status: VatStatus }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`vat.status.${status}`)}</Badge>;
}

/** The VAT of an order: its rate, or what replaces one (reverse charge, not applicable, held for review). */
export function orderVatLabel(order: Pick<Order, 'vat_scheme' | 'vat_rate'>, t: (key: string, options?: Record<string, unknown>) => string, locale = 'en'): string {
  switch (order.vat_scheme) {
    case 'standard':
      return t('vat.rate', { rate: new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { maximumFractionDigits: 1 }).format(Number(order.vat_rate) * 100) });
    case 'reverse_charge':
      return t('vat.scheme.reverse_charge');
    case 'outside_scope':
      return t('vat.scheme.outside_scope');
    case 'review':
      return t('vat.scheme.review');
    default:
      return '—';
  }
}

/** The three cells of an order's amount: excluding VAT, the VAT, the total including it. */
export function OrderAmounts({ order }: { order: Order }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const held = order.vat_scheme === 'review';
  return (
    <>
      <td className="px-3 py-2 align-middle font-mono text-[11.5px]">{money(order.amount, order.currency, l)}</td>
      <td className="px-3 py-2 align-middle font-mono text-[11.5px]" data-testid={`vat-${order.id}`}>
        {held ? <Badge tone="warn">{orderVatLabel(order, t, l)}</Badge> : (
          <>
            {orderVatLabel(order, t, l)}
            {order.vat_scheme === 'standard' && <span className="text-faint"> · {money(order.vat_amount, order.currency, l)}</span>}
          </>
        )}
      </td>
      <td className="px-3 py-2 align-middle font-mono text-[11.5px]">{money(order.total, order.currency, l)}</td>
    </>
  );
}

/** « Numéro valide » / « Numéro non valide », each confirmed; `onDone` gets the orders the decision released. */
export function VatDecisionButtons({ organizationId, number, onDone, onError }: { organizationId: number; number: string; onDone: (decision: VatDecision) => void; onError: (err: unknown) => void }) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const decide = async (valid: boolean) => {
    setBusy(true);
    try {
      onDone(await api.decideVat(organizationId, valid));
      setAsking(null);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex justify-end gap-1">
      <Button variant="primary" onClick={() => setAsking(true)} data-testid={`vat-valid-${organizationId}`}>
        {t('vat.markValid')}
      </Button>
      <Button variant="danger" onClick={() => setAsking(false)} data-testid={`vat-invalid-${organizationId}`}>
        {t('vat.markInvalid')}
      </Button>
      <Confirm
        open={asking !== null}
        title={t(asking ? 'vat.validTitle' : 'vat.invalidTitle', { number })}
        message={t(asking ? 'vat.validMessage' : 'vat.invalidMessage')}
        confirmLabel={t(asking ? 'vat.markValid' : 'vat.markInvalid')}
        danger={asking === false}
        busy={busy}
        onCancel={() => setAsking(null)}
        onConfirm={() => asking !== null && void decide(asking)}
      />
    </div>
  );
}

/** The sentence staff read after a decision or a VIES check. */
export function decisionNotice(decision: VatDecision, t: (key: string, options?: Record<string, unknown>) => string): string {
  const status = t(`vat.status.${decision.vat_status}`);
  return decision.released_orders.length
    ? t('vat.decidedReleased', { status, orders: decision.released_orders.map((id) => `#${id}`).join(', ') })
    : t('vat.decided', { status });
}
